// Global theme configuration for the application
// Asogro brand — orange (#f97316) primary, slate secondary, emerald success.
// This palette mirrors tailwind.config.js so Tailwind and Ant Design stay in sync.

export const theme = {
  colors: {
    primary: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316', // Main orange
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
    },
    secondary: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b', // Main slate
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    accent: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b', // Main amber
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
    },
    neutral: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
    },
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#22c55e',
  },

  gradients: {
    primary: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', // orange
    primaryLight: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)', // light orange
    secondary: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)', // bright orange
    accent: 'linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%)', // yellow to amber
    card: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)', // darker orange
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fef3c7 100%)', // warm neutrals
  },

  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    card: '0 4px 20px rgba(249, 115, 22, 0.1)', // orange-tinted shadow for cards
    cardHover: '0 8px 30px rgba(249, 115, 22, 0.15)',
  },

  borderRadius: {
    sm: '0.375rem', // 6px
    md: '0.5rem', // 8px
    lg: '0.75rem', // 12px
    xl: '1rem', // 16px
    '2xl': '1.5rem', // 24px
    '3xl': '1.75rem', // 28px
    full: '9999px',
  },

  spacing: {
    xs: '0.25rem', // 4px
    sm: '0.5rem', // 8px
    md: '1rem', // 16px
    lg: '1.5rem', // 24px
    xl: '2rem', // 32px
    '2xl': '3rem', // 48px
    '3xl': '4rem', // 64px
  },

  typography: {
    fontFamily: {
      sans: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    },
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // Ant Design specific token overrides
  antd: {
    colorPrimary: '#f97316', // orange-500
    colorSuccess: '#22c55e', // green-500
    colorWarning: '#f59e0b', // amber-500
    colorError: '#ef4444', // red-500
    colorInfo: '#22c55e', // green-500
    borderRadius: 12,
    controlHeight: 40,
    fontSize: 14,
    fontFamily:
      '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  // Dark mode color overrides
  dark: {
    colors: {
      background: '#0f172a', // slate-900
      surface: '#1e293b', // slate-800
      surfaceHover: '#334155', // slate-700
      border: '#334155', // slate-700
      text: {
        primary: '#f1f5f9', // slate-100
        secondary: '#cbd5e1', // slate-300
        muted: '#94a3b8', // slate-400
      },
    },
    antd: {
      colorBgContainer: '#1e293b', // slate-800
      colorBgElevated: '#334155', // slate-700
      colorBgLayout: '#0f172a', // slate-900
      colorBorder: '#334155', // slate-700
      colorText: '#f1f5f9', // slate-100
      colorTextSecondary: '#cbd5e1', // slate-300
      colorTextTertiary: '#94a3b8', // slate-400
    },
  },
};

// CSS class helpers — orange-based to match the brand
export const themeClasses = {
  // Backgrounds
  bgPrimary: 'bg-gradient-to-br from-primary-400 via-primary-500 to-amber-400',
  bgPrimaryLight: 'bg-gradient-to-br from-primary-50 via-primary-100 to-amber-50',
  bgCard: 'bg-white rounded-3xl shadow-xl',
  bgCardLight: 'bg-white rounded-3xl shadow-xl', // Always light theme
  bgCardGradient: 'bg-gradient-to-br from-primary-600 to-primary-700',

  // Text colors
  textPrimary: 'text-primary-600 dark:text-primary-400',
  textSecondary: 'text-secondary-600 dark:text-secondary-400',
  textAccent: 'text-amber-500 dark:text-amber-400',
  textMuted: 'text-secondary-500 dark:text-secondary-400',
  textDark: 'text-secondary-800 dark:text-white',
  textDarkLight: 'text-secondary-800', // Always light theme

  // Buttons
  btnPrimary:
    'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 border-0 shadow-md',
  btnSecondary: 'border-secondary-200 hover:border-primary-400',
  btnRounded: 'rounded-xl',

  // Inputs
  inputRounded: 'rounded-xl border-secondary-200',
  inputFocus: 'focus:border-primary-500 focus:ring-primary-500',

  // Links
  link: 'text-primary-600 hover:text-primary-700',
  linkBold: 'text-primary-600 hover:text-primary-700 font-semibold',

  // Cards
  card: 'bg-white dark:bg-secondary-800 rounded-3xl shadow-xl border border-secondary-100 dark:border-secondary-700',
  cardLight: 'bg-white rounded-3xl shadow-xl border border-secondary-100', // Always light theme
  cardDashboard: 'bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl shadow-2xl',

  // Decorative elements
  orb: 'rounded-full opacity-40 blur-xl',
  floatingElement: 'shadow-lg transform',
};

export default theme;

// Light-only theme classes for auth pages
export const lightThemeClasses = {
  // Backgrounds (always light)
  bgPrimary: 'bg-gradient-to-br from-primary-400 via-primary-500 to-amber-400',
  bgPrimaryLight: 'bg-gradient-to-br from-primary-50 via-primary-100 to-amber-50',
  bgCard: 'bg-white rounded-3xl shadow-xl',

  // Text colors (always light)
  textPrimary: 'text-primary-600',
  textSecondary: 'text-secondary-600',
  textAccent: 'text-amber-500',
  textMuted: 'text-secondary-500',
  textDark: 'text-secondary-800',

  // Buttons (same as themeClasses - gradients work in light)
  btnPrimary:
    'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 border-0 shadow-md',
  btnSecondary: 'border-secondary-200 hover:border-primary-400',
  btnRounded: 'rounded-xl',

  // Links (always light)
  link: 'text-primary-600 hover:text-primary-700',
  linkBold: 'text-primary-600 hover:text-primary-700 font-semibold',

  // Cards (always light)
  card: 'bg-white rounded-3xl shadow-xl border border-secondary-100',
  cardDashboard: 'bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl shadow-2xl',

  // Decorative elements
  orb: 'rounded-full opacity-40 blur-xl',
  floatingElement: 'shadow-lg transform',
};
